// Print an HTML fragment in a popup, ALWAYS with background graphics on.
//
// - `print-color-adjust: exact !important` forces the browser to print background
//   colors/images even when the print dialog's "Background graphics" box is off.
// - A `<base href>` makes root-relative asset paths (e.g. /letterhead.png,
//   /assets/stamp.png) resolve against the app origin inside the popup.
// - It waits for every image to finish loading (with a safety timeout) before
//   calling print(), so the letterhead / stamp / signature are never missing.
export function printHtml(title: string, bodyHtml: string, onDone?: () => void): void {
  const win = window.open('', '_blank', 'width=900,height=1200');
  if (!win) {
    alert('Please allow pop-ups for this site to print.');
    onDone?.();
    return;
  }

  const css = `
    html, body, * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    body { margin: 0; padding: 0; background: #fff; }
    @media print { @page { size: A4 portrait; margin: 0; } }
    /* Multi-page invoices (ConsumableInvoiceDocument) emit one [data-invoice-page]
       per sheet — force each onto its own printed page. No-op for single-page docs. */
    [data-invoice-page] { break-after: page; page-break-after: always; break-inside: avoid; }
    [data-invoice-page]:last-child { break-after: auto; page-break-after: auto; }
  `;

  win.document.open();
  win.document.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"/>` +
    `<base href="${window.location.origin}/"/>` +
    `<title>${title}</title><style>${css}</style></head>` +
    `<body>${bodyHtml}</body></html>`
  );
  win.document.close();

  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    win.focus();
    win.print();
    onDone?.();
  };

  const imgs = Array.from(win.document.images || []);
  if (imgs.length === 0) {
    setTimeout(doPrint, 250);
    return;
  }
  let pending = imgs.length;
  const one = () => { if (--pending <= 0) doPrint(); };
  imgs.forEach((img) => {
    if (img.complete) one();
    else { img.onload = one; img.onerror = one; }
  });
  // Safety net: print even if an image stalls.
  setTimeout(doPrint, 3000);
}
